import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import { Faker, base, en } from '@faker-js/faker'

import * as loader from '../loader'

const faker = new Faker({ locale: [en, base], seed: 0 })

// NOTE: this must match what is in the CSS
const LOADING_ANIMATION_DURATION = 2.4e3

/**
 * A cool looking splash screen for the initial site load
 */
@ngCore.Component({
  imports: [loader.Component],
  selector: 'atn-ds-splash-screen',
  standalone: true,
  styleUrls: ['./component.scss'],
  templateUrl: './component.html',
})
export class Component implements ngCore.AfterViewInit, ngCore.OnDestroy {
  /**
   * Main title to display
   */
  public readonly titleSg = ngCore.input.required<string>({
    alias: 'title',
  })

  /**
   * Subtitle to display, if there is one
   */
  public readonly subtitleSg = ngCore.input<string>(undefined, {
    alias: 'subtitle',
  })

  /**
   * Main color to use, if there is one
   */
  public readonly colorSg = ngCore.input<string>(undefined, {
    alias: 'color',
  })

  /**
   * Logo {@link image} to display
   */
  public readonly logoSg = ngCore.input<ents.core.display.Image>(
    {
      alt: 'Attain Apps logo mark',
      src: 'assets/img/attain-mark.png',
    } satisfies ents.core.display.Image,
    {
      alias: 'logo',
    },
  )

  /**
   * When `true`, we remove the delays on some of the initial animations
   *
   * Useful when the splash screen will only be presented for a short period of
   * time (e.g. < 2 seconds)
   */
  public readonly noDelaySg = ngCore.input<boolean>(false, {
    alias: 'noDelay',
  })

  /**
   * We go through these in order, and then cycle through
   * {@link repeatableLoadingMessagesSg}
   */
  public readonly initialLoadingMessagesSg = ngCore.input<readonly string[]>(
    [],
    {
      alias: 'initialLoadingMessages',
    },
  )

  /**
   * After we have completed the {@link initialLoadingMessagesSg}, we cycle
   * through these
   */
  public readonly repeatableLoadingMessagesSg = ngCore.input<readonly string[]>(
    [],
    {
      alias: 'repeatableLoadingMessages',
    },
  )

  /**
   * Used to determine {@link loadingMessageSg}
   */
  public readonly loadingStepSg = ngCore.signal(0)
  /**
   * Used to increment {@link loadingStepSg}
   */
  private _loadingStepIncrementer: unknown | undefined

  /**
   * Currently displayed loading message
   */
  public readonly loadingMessageSg = ngCore.computed(
    (
      step = this.loadingStepSg(),
      initialLoadingMessages = this.initialLoadingMessagesSg(),
      repeatableLoadingMessages = this.repeatableLoadingMessagesSg(),
    ) =>
      step < initialLoadingMessages.length
        ? initialLoadingMessages[step]
        : repeatableLoadingMessages[
            (step - initialLoadingMessages.length) %
              repeatableLoadingMessages.length
          ],
  )

  /**
   * Randomly generated particles to show in the background
   */
  public readonly particlesSg = ngCore.signal(
    faker.helpers.multiple<Particle>(
      () => ({
        animationDelay: faker.number.int({ max: 15, min: 0 }),
        animationDuration: faker.number.int({ max: 25, min: 15 }),
        left: faker.number.int({ max: 100, min: 0 }),
      }),
      { count: 50 },
    ),
  )

  /**
   * __NOTE:__ we set up {@link _loadingStepIncrementer} here so that it is in
   * sync with the animation
   */
  public ngAfterViewInit(): void {
    this._loadingStepIncrementer = setInterval(() => {
      this.loadingStepSg.update(step => step + 1)
    }, LOADING_ANIMATION_DURATION)
  }

  /**
   * Cleans up {@link _loadingStepIncrementer}
   */
  public ngOnDestroy(): void {
    // NOTE: node and browsers have different types for this. We can't reference
    // the node types because that would break when we build the frontend, but
    // the node types seem to be applied in VS Code, so we just type it as
    // `unknown` and cast it here
    clearInterval(this._loadingStepIncrementer as number)
  }
}

interface Particle {
  readonly left: number
  readonly animationDelay: number
  readonly animationDuration: number
}
